import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

interface Row {
  id: string;
  status: string;
  bdr_name_verified: string;
  vendor_id: string;
  prospect_email: string;
  company_name: string;
  discovery_date: string;
  sfdc_opportunity_id: string | null;
  created_at: string;
}

export function Admin() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("");

  useEffect(() => {
    api.listAdminSubmissions()
      .then((r) => setRows(r.submissions as Row[]))
      .catch((e) => setErr(e.message ?? "Unable to load submissions"));
  }, []);

  const visible = rows?.filter((r) =>
    !filter || r.status === filter
  ) ?? null;

  return (
    <div className="min-h-screen">
      <header className="px-6 py-4 flex items-center justify-between border-b border-lilac/40 bg-white/60 backdrop-blur">
        <Logo />
        <span className="text-xs font-bold uppercase tracking-widest text-grape">
          Admin · Submissions
        </span>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        <div className="mb-4 flex gap-2 flex-wrap">
          {["", "success", "processing", "duplicate", "manual_review", "failed"].map((s) => (
            <button
              key={s}
              className={`px-3 py-1.5 rounded text-sm font-bold transition ${
                filter === s
                  ? "bg-brand-gradient text-white"
                  : "bg-white text-midnight border border-lilac/40"
              }`}
              onClick={() => setFilter(s)}
            >
              {s || "All"}
            </button>
          ))}
        </div>

        {err && <div className="card p-4 text-fuscia">{err}</div>}
        {!rows && !err && <div className="card p-4 text-grape">Loading…</div>}

        {visible && (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-midnight text-white">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">BDR</th>
                  <th className="text-left px-4 py-3">Vendor</th>
                  <th className="text-left px-4 py-3">Prospect</th>
                  <th className="text-left px-4 py-3">Company</th>
                  <th className="text-left px-4 py-3">Discovery</th>
                  <th className="text-left px-4 py-3">Opp</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F8F6FC]"}>
                    <td className="px-4 py-2 text-grape whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="px-4 py-2">{r.bdr_name_verified}</td>
                    <td className="px-4 py-2">{r.vendor_id}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.prospect_email}</td>
                    <td className="px-4 py-2">{r.company_name}</td>
                    <td className="px-4 py-2">{r.discovery_date}</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {r.sfdc_opportunity_id ?? "—"}
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-grape">
                      No submissions match this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    success: "bg-lime/20 text-midnight",
    processing: "bg-iris/40 text-midnight",
    duplicate: "bg-mustard/20 text-midnight",
    manual_review: "bg-fuscia/20 text-midnight",
    failed: "bg-fuscia text-white",
  };
  const cls = map[status] ?? "bg-lilac/40 text-midnight";
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold ${cls}`}>{status}</span>
  );
}
